import {
  CheckroomOutlined,
  Inventory2Outlined,
  LocalOfferOutlined,
  RepeatOutlined,
  RunningWithErrorsOutlined,
  SearchOffOutlined,
  SecurityOutlined,
  TouchApp,
} from "@mui/icons-material";
import { Box, Divider, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { SidebarItem } from "@/components/layout/Sidebar/SidebarItem";
import { SidebarSection } from "@/components/layout/Sidebar/SidebarSection";
import { paths } from "@/routes/paths";
import { UserRole } from "@/utils/roleManager";

interface ReportsSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  userHasPermission: (role: UserRole) => boolean;
  isMobile?: boolean;
  onItemSelect?: () => void;
}

const reportMinimumRoles = [UserRole.STORE_ADMIN] as const;

export const hasAvailableReports = (userHasPermission: (role: UserRole) => boolean) =>
  reportMinimumRoles.some((role) => userHasPermission(role));

export const ReportsSection: React.FC<ReportsSectionProps> = ({
  isExpanded,
  onToggle,
  userHasPermission,
  isMobile = false,
  onItemSelect,
}) => {
  const { t } = useTranslation();

  if (!hasAvailableReports(userHasPermission)) {
    return null;
  }

  return (
    <SidebarSection
      title={t("nav.sections.reports")}
      isExpanded={isExpanded}
      onToggle={onToggle}
      isMobile={isMobile}
    >
      <SidebarItem
        label={t("layout.sidebar.reportsSection.fields.sessions")}
        path={paths.reports.sessions}
        icon={<CheckroomOutlined />}
        minimumRole={UserRole.STORE_ADMIN}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("layout.sidebar.reportsSection.fields.canTFind")}
        path="/reports/cant-find"
        icon={<SearchOffOutlined />}
        minimumRole={UserRole.STORE_ADMIN}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("layout.sidebar.reportsSection.fields.missed")}
        path="/reports/missed"
        icon={<RunningWithErrorsOutlined />}
        minimumRole={UserRole.STORE_ADMIN}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("layout.sidebar.reportsSection.fields.outOfStock")}
        path="/reports/out-of-stock"
        icon={<Inventory2Outlined />}
        minimumRole={UserRole.STORE_ADMIN}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("layout.sidebar.reportsSection.fields.popularProducts")}
        path="/reports/popular-products"
        icon={<LocalOfferOutlined />}
        minimumRole={UserRole.STORE_ADMIN}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <Box sx={{ display: "flex", alignItems: "center", my: 1, mx: 2 }}>
        <Divider sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          sx={{ px: 1, fontSize: 10, opacity: 0.6, textTransform: "uppercase" }}
        >
          {t("layout.sidebar.reportsSection.fields.featureReports")}
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>
      <SidebarItem
        label={t("layout.sidebar.reportsSection.fields.clickAndCollect")}
        path={paths.reports.clickAndCollect}
        icon={<TouchApp />}
        minimumRole={UserRole.STORE_ADMIN}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("layout.sidebar.reportsSection.fields.continuousReads")}
        path="/reports/continuous-reads"
        icon={<RepeatOutlined />}
        minimumRole={UserRole.STORE_ADMIN}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("layout.sidebar.reportsSection.fields.suspiciousActivity")}
        path="/reports/suspicious-activity"
        icon={<SecurityOutlined />}
        minimumRole={UserRole.STORE_ADMIN}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
    </SidebarSection>
  );
};
